import { optimizelyClient } from './client';
import { events } from './events';

/**
 * Set the Optimizely user id and attributes to be used in subsequent api calls.
 *
 * @param {object} user - User traits object (map of user traits).
 * @param {number} user.id - User's id.
 * @param {object} user.attributes - Remaining user traits.
 * @returns {Promise} The Optimizely onReady promise.
 */
export const setUser = ({ id, ...attributes }) => {
  optimizelyClient.setUser?.({
    id: id.toString(),
    attributes,
  });
  return optimizelyClient.onReady?.();
};

/**
 * Get list of all features enabled for user.
 *
 * @returns {Array<string>} Array of enabled feature names.
 */
export const getEnabledFeatures = () =>
  optimizelyClient?.getEnabledFeatures?.() || [];

/**
 * Get enabled features and their variable values for whichever variation the
 * user is assigned to ("On" or "Off").
 *
 * @returns {object} Hash of enabled feature name => Hash of feature variables.
 */
export const getEnabledFeaturesAndVariables = () =>
  getEnabledFeatures().reduce((acc, feature) => {
    const vars = getFeatureVariables(feature);
    // If there are no vars, set feature value to true, otherwise vars.
    // This ensures enabled feature information doesn't get stripped when
    // sent to datadog (empty objects get stripped).
    acc[feature] = Object.keys(vars).length ? vars : true;
    return acc;
  }, {});

/**
 * Get all the variables set on the feature in Optimizely.
 *
 * @param {string} feature - The feature name.
 * @returns {object} Hash of all variables set for the feature.
 */
export const getFeatureVariables = (feature) =>
  optimizelyClient.getAllFeatureVariables?.(feature);

/**
 *
 * @param {string} feature - The feature name.
 * @returns {boolean} Boolean indicating whether feature is enabled or not.
 */
export const isFeatureEnabled = (feature) =>
  optimizelyClient.isFeatureEnabled?.(feature);

/**
 * Triggers the experimentActivated event.
 *
 * @param {string} experiment - The experiment name.
 */
export const activateExperiment = (experiment) => {
  events.experimentActivated({
    experiment,
    variables: getFeatureVariables(experiment),
    variation: getAssignedVariation(experiment),
  });
};

/**
 * Triggers the experimentVariationViewed event.
 *
 * @param {string} experiment - The experiment name.
 */
export const viewExperiment = (experiment) => {
  events.experimentVariationViewed({
    experiment,
    variables: getFeatureVariables(experiment),
    variation: getAssignedVariation(experiment),
  });
};

const getAssignedVariation = (experiment) =>
  isFeatureEnabled(experiment) ? 'test' : 'control';

export const isReady = () => optimizelyClient.isReady();

export const onReady = async () => optimizelyClient.onReady();
